import React, { useState } from 'react';
import IconFont from '$/components/IconFont';
import NextImage from '$/components/NextImage';
import { getMyIpDetailMedia } from '$/domains/myIp/detailMedia';
import { getMyIpMediaUrl } from '$/domains/myIp/mediaUrl';
import { getMyIpProfileDisplay } from '$/domains/myIp/profileDisplay';
import type { MyIpItem } from '$/domains/myIp/types';
import type { MyIpAssetDetailViewState } from '$/domains/myIp/returnState';
import { useLanguageStore } from '$/store/languageStore';
import { useMyIpMediaPreview } from '$/components/MyIpMediaPreview/useMyIpMediaPreview';
import { MyIpMediaPreviewBoundary } from '$/components/MyIpMediaPreview/Boundary';
import MyIpMediaGallery from '$/components/MyIpMediaGallery';
import { useMyIpCopy } from '../useMyIpCopy';
import { useCardAction } from '../useCardAction';
import styles from './index.module.scss';

/** 右栏页签和操作固定，只有素材与设定滚动；恢复状态继续由资产宿主管理。 */
function AssetDetailBody({
  item, coverUrl, detailHref, initialView, onViewSettings, onIpDetailClick, onApplyToCanvas,
  applyToCanvasDisabled = false, actionLabel, hideMaterials = false, scrollRef,
}: {
  item: MyIpItem;
  coverUrl?: string;
  detailHref: string;
  initialView?: MyIpAssetDetailViewState;
  onViewSettings: (view: MyIpAssetDetailViewState) => void;
  onIpDetailClick?: (item: MyIpItem) => void;
  onApplyToCanvas?: (item: MyIpItem) => Promise<void>;
  applyToCanvasDisabled?: boolean;
  actionLabel?: string;
  /** 爆款复刻商品槽选 IP 时隐藏「更多素材」，避免误选非主图资源。 */
  hideMaterials?: boolean;
  scrollRef?: React.RefObject<HTMLDivElement>;
}) {
  const { asset: copy, detail: detailCopy, media: mediaCopy } = useMyIpCopy();
  const translate = useLanguageStore((state) => state.languageStore.smartcatTranslationText);
  const mainImageLabel = translate((value) => value.main_image);
  const nameLabel = translate((value) => value.robo_juese_name);
  const media = getMyIpDetailMedia(item);
  const profile = getMyIpProfileDisplay(item.profile);
  const mainUrl = getMyIpMediaUrl(coverUrl) || media.coverUrl;
  const mainDisplayUrl = media.coverWatermarkUrl || mainUrl;
  const [tab, setTab] = useState<'settings' | 'materials'>(
    hideMaterials ? 'settings' : (initialView?.tab ?? 'settings'),
  );
  const [mode, setMode] = useState<'main' | 'threeViews'>(initialView?.mode === 'threeViews' && media.threeViewUrls.length ? 'threeViews' : 'main');
  const [index, setIndex] = useState(initialView?.imageIndex ?? 0);
  const [failedUrls, setFailedUrls] = useState<string[]>([]);
  const [previewFailure, setPreviewFailure] = useState<string>();
  const applyToCanvas = useCardAction(onApplyToCanvas ? () => onApplyToCanvas(item) : undefined);
  const currentIndex = Math.min(index, Math.max(0, media.threeViewUrls.length - 1));
  const images = [
    {
      id: 'main', kind: 'image', url: mainUrl || '', name: item.name,
      watermarkUrl: media.coverWatermarkUrl,
      displayUrl: mainDisplayUrl || '',
    },
    ...media.threeViewUrls.map((url, position) => ({
      id: `three_view:${url}`, kind: 'image', url, name: detailCopy.threeViews,
      watermarkUrl: media.threeViewWatermarkUrls?.[position],
      displayUrl: media.threeViewWatermarkUrls?.[position] || url,
    })),
  ];
  const activeIndex = mode === 'main' ? 0 : currentIndex + 1;
  // 隐藏素材页签时始终停留在设定预览，避免恢复态把材料页签带回。
  const activeTab = hideMaterials ? 'settings' : tab;
  const previewImage = useMyIpMediaPreview(activeTab === 'settings' ? images.filter((image) => !failedUrls.includes(image.url)) : [], {
    scopeKey: JSON.stringify([item.ipId, item.assetType]),
    onMediaError: (url) => { setPreviewFailure(url); setFailedUrls((previous) => [...previous, url]); },
  });
  const settings = [
    { label: nameLabel, value: item.name },
    { label: detailCopy.identity, value: profile.identity || item.summary },
    { label: detailCopy.personality, value: profile.personality.join('、') },
    { label: detailCopy.appearance, value: profile.appearance },
    { label: detailCopy.visualStyle, value: profile.visualStyle },
    { label: detailCopy.speechStyle, value: profile.speechStyle },
    { label: detailCopy.backstory, value: profile.backstory },
    ...profile.moreSettings.map((setting) => ({ label: setting.key, value: setting.value })),
  ].filter((setting) => setting.value);

  /** 翻页仅改变现有主图/三视图的索引，不把预览占位或资产 ID 当作图片。 */
  const changeImage = (offset: number) => {
    const next = (activeIndex + offset + images.length) % images.length;
    setMode(next === 0 ? 'main' : 'threeViews');
    if (next > 0) setIndex(next - 1);
  };
  /** 切换页签从内容顶部开始，避免更多素材继承设定正文的滚动位置。 */
  const changeTab = (next: 'settings' | 'materials') => {
    setTab(next);
    const scroller = scrollRef?.current;
    if (scroller) scroller.scrollTop = 0;
  };
  /** 大图和缩略图共用失效状态，原地址失败后不再打开空预览。 */
  const failImage = (url: string) => setFailedUrls((previous) => [...previous, url]);
  /** 查看角色设定前先上报 IP 详情点击，再保存宿主返回所需的界面状态。 */
  const handleViewSettingsClick = () => {
    onIpDetailClick?.(item);
    onViewSettings({ tab, mode, imageIndex: currentIndex });
  };
  /** 资产角色应用复用异步动作锁，避免连续点击在同一画布重复创建 IP 组。 */
  const handleApplyToCanvasClick = () => {
    if (applyToCanvasDisabled) return;
    applyToCanvas.run();
  };

  return (
    <>
      {!hideMaterials && (
        <div className={styles.detailHeader}>
          <div className={styles.detailTabs} role="group" aria-label={detailCopy.characterSettings}>
            <button type="button" aria-pressed={tab === 'settings'} onClick={() => changeTab('settings')}>{mediaCopy.settings}</button>
            <button type="button" aria-pressed={tab === 'materials'} onClick={() => changeTab('materials')}>{mediaCopy.more}</button>
          </div>
        </div>
      )}
      <div ref={scrollRef} className={styles.detailScroll}>
        {activeTab === 'settings' ? (
          <section aria-label={mediaCopy.settings}>
            <div className={styles.imageArea}>
              <div className={styles.imageStrip}>
                {images.slice(activeIndex, activeIndex + 2).map((image, position) => {
                  const available = Boolean(image.url && !failedUrls.includes(image.url));
                  return (
                    <button key={image.id} type="button" className={styles.enlargeImage} aria-label={image.id === 'main' ? mediaCopy.enlargeMain : mediaCopy.enlargeThreeView} disabled={!available} tabIndex={position === 0 ? 0 : -1} onClick={() => previewImage(image)}>
                      {available ? <NextImage className={styles.detailCover} src={image.displayUrl} alt={image.name} crossOrigin="anonymous" onError={() => failImage(image.url)} /> : <span className={styles.detailCover} role="img" aria-label={mediaCopy.unavailable}>{mediaCopy.unavailable}</span>}
                    </button>
                  );
                })}
              </div>
              {images.length > 1 && (
                <div className={styles.imageNavigation}>
                  {activeIndex > 0 && <button className={styles.previousImage} type="button" aria-label={activeIndex === 1 ? mainImageLabel : `${detailCopy.threeViews} ${currentIndex} / ${media.threeViewUrls.length}`} onClick={() => changeImage(-1)}><IconFont name="arrowLeftBold" fontSize={20} /></button>}
                  <button className={styles.nextImage} type="button" aria-label={activeIndex === images.length - 1 ? mainImageLabel : `${detailCopy.threeViews} ${activeIndex + 1} / ${media.threeViewUrls.length}`} onClick={() => changeImage(1)}><IconFont name="arrowRightBold" fontSize={20} /></button>
                </div>
              )}
            </div>
            <div className={styles.previewModes} role="group" aria-label={mediaCopy.preview}>
              {([{ value: 'main', label: mainImageLabel, url: mainUrl, displayUrl: mainDisplayUrl }, { value: 'threeViews', label: detailCopy.threeViews, url: media.threeViewUrls[0], displayUrl: media.threeViewWatermarkUrls?.[0] || media.threeViewUrls[0] }] as const).map((option) => (
                <button key={option.value} type="button" aria-pressed={mode === option.value} disabled={option.value === 'threeViews' && !media.threeViewUrls.length} onClick={() => setMode(option.value)}>
                  {option.url && !failedUrls.includes(option.url) ? <NextImage src={option.displayUrl || option.url} alt="" crossOrigin="anonymous" onError={() => failImage(option.url!)} /> : <span className={styles.modePlaceholder} aria-hidden />}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
            <dl className={styles.settingsList}>
              {/* 服务端自定义设定允许同名同值，按稳定显示顺序区分重复条目。 */}
              {settings.map((setting, position) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`${position}:${setting.label}`}>
                  <dt>{setting.label}</dt>
                  <dd>{setting.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : <MyIpMediaGallery key={item.ipId} item={item} urls={media.resourceUrls} compact />}
        {previewFailure && images.some((image) => image.url === previewFailure) && <p role="alert">{mediaCopy.unavailable}</p>}
      </div>
      <div className={`${styles.actions} ${onApplyToCanvas ? styles.actionsWithApply : ''}`}>
        <a href={detailHref} onClick={handleViewSettingsClick}>{copy.viewSettings}</a>
        {onApplyToCanvas && (
          <button type="button" className={styles.applyToCanvas} disabled={applyToCanvasDisabled || applyToCanvas.pending} onClick={handleApplyToCanvasClick}>
            {actionLabel || copy.applyToCanvas}
          </button>
        )}
      </div>
    </>
  );
}

/** 资产右栏独立消费时挂载公共预览，应用宿主或嵌套图库已经提供时共用同一实例。 */
export default function AssetDetailContent({
  item, coverUrl, detailHref, initialView, onViewSettings, onIpDetailClick, onApplyToCanvas,
  applyToCanvasDisabled, actionLabel, hideMaterials, scrollRef,
}: Parameters<typeof AssetDetailBody>[0]) {
  return <MyIpMediaPreviewBoundary><AssetDetailBody item={item} coverUrl={coverUrl} detailHref={detailHref} initialView={initialView} onViewSettings={onViewSettings} onIpDetailClick={onIpDetailClick} onApplyToCanvas={onApplyToCanvas} applyToCanvasDisabled={applyToCanvasDisabled} actionLabel={actionLabel} hideMaterials={hideMaterials} scrollRef={scrollRef} /></MyIpMediaPreviewBoundary>;
}
